/**
 * Firestore Hook
 *
 * Custom React hook for Firestore database operations
 * Handles project CRUD operations and user data
 */

import { useCallback, useState } from 'react';
import type { Project, User } from '@canvas/shared-types';
import {
  projectPath,
  userProjectsPath,
  userPath,
} from '@canvas/shared-types';
import {
  getClientDB,
  getClientAuth,
  getClientStorage,
} from '@canvas/firebase-config/client';
import {
  collection,
  query,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  writeBatch,
  Timestamp,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { encodeFirestoreValue, decodeFirestoreValue } from '@/lib/firestoreSafe';

/** Firestore modular paths must not start with `/`. */
function fsPath(segments: string): string {
  return segments.replace(/^\/+/, '');
}

export interface UseFirestoreReturn {
  // State
  isLoading: boolean;
  error: string | null;

  // Project operations
  saveProject: (project: Project) => Promise<void>;
  loadProject: (projectId: string) => Promise<Project>;
  loadProjects: () => Promise<Project[]>;
  deleteProject: (projectId: string) => Promise<void>;
  subscribeToProject: (projectId: string, callback: (project: Project) => void) => Unsubscribe | null;
  subscribeToProjects: (callback: (projects: Project[]) => void) => Unsubscribe | null;

  // User operations
  loadUserData: () => Promise<User | null>;
  updateUserData: (userData: Partial<User>) => Promise<void>;

  // Utilities
  clear: () => void;
}

/**
 * Firestore Hook
 *
 * @example
 * const { saveProject, loadProject, loadProjects, error, isLoading } = useFirestore();
 *
 * // Save a project
 * await saveProject(projectData);
 *
 * // Load all user projects
 * const projects = await loadProjects();
 */
export function useFirestore(): UseFirestoreReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const db = getClientDB();
  const storage = getClientStorage();

  /**
   * Get the current user's UID (live auth — not a stale render capture)
   * Throws if not authenticated
   */
  const getUserId = useCallback(() => {
    const user = getClientAuth().currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }
    return user.uid;
  }, []);

  /**
   * Convert Firestore timestamps to Date objects and restore nested arrays
   */
  const convertDates = (obj: any): Project => {
    const decoded = decodeFirestoreValue(obj) as any;
    return {
      ...decoded,
      createdAt: decoded.createdAt?.toDate?.() || new Date(decoded.createdAt),
      lastModifiedAt:
        decoded.lastModifiedAt?.toDate?.() || new Date(decoded.lastModifiedAt),
      updatedAt: decoded.updatedAt?.toDate?.() || new Date(decoded.updatedAt),
    };
  };

  /**
   * Save a project to Firestore
   *
   * Creates new project or updates existing one
   *
   * @param project - Project to save
   * @throws Error if not authenticated or save fails
   *
   * @example
   * await saveProject({
   *   projectId: 'proj123',
   *   userId: 'uid123',
   *   name: 'My Design',
   *   canvasData: {...},
   *   // ... other fields
   * });
   */
  const saveProject = useCallback(
    async (project: Project): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const userId = getUserId();

        if (project.userId !== userId) {
          throw new Error('Cannot save project for another user');
        }

        const projectRef = doc(db, fsPath(projectPath(userId, project.projectId)));

        // Fabric JSON often contains `undefined` and nested arrays (paths).
        // Firestore rejects both — strip undefined via JSON, then encode nested arrays.
        const cleaned = JSON.parse(
          JSON.stringify({
            ...project,
            createdAt: undefined,
            lastModifiedAt: undefined,
            updatedAt: undefined,
          })
        ) as Record<string, unknown>;

        const encoded = encodeFirestoreValue(cleaned) as Record<string, unknown>;

        // Prepare data with Firestore timestamps
        const data = {
          ...encoded,
          createdAt: Timestamp.fromDate(
            project.createdAt instanceof Date
              ? project.createdAt
              : new Date(project.createdAt)
          ),
          lastModifiedAt: Timestamp.fromDate(
            project.lastModifiedAt instanceof Date
              ? project.lastModifiedAt
              : new Date(project.lastModifiedAt)
          ),
          updatedAt: Timestamp.fromDate(
            project.updatedAt instanceof Date
              ? project.updatedAt
              : new Date(project.updatedAt)
          ),
        };

        // Use set with merge to handle both create and update
        await setDoc(projectRef, data, { merge: true });

        setIsLoading(false);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to save project';
        setError(errorMessage);
        setIsLoading(false);
        throw err;
      }
    },
    [db, getUserId]
  );

  /**
   * Load a single project from Firestore
   *
   * @param projectId - ID of project to load
   * @returns The loaded project
   * @throws Error if project not found or load fails
   *
   * @example
   * const project = await loadProject('proj123');
   */
  const loadProject = useCallback(
    async (projectId: string): Promise<Project> => {
      setIsLoading(true);
      setError(null);

      try {
        const userId = getUserId();
        const projectRef = doc(db, fsPath(projectPath(userId, projectId)));
        const snapshot = await getDoc(projectRef);

        if (!snapshot.exists()) {
          throw new Error(`Project '${projectId}' not found`);
        }

        const project = convertDates(snapshot.data());
        setIsLoading(false);

        return project;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load project';
        setError(errorMessage);
        setIsLoading(false);
        throw err;
      }
    },
    [db, getUserId]
  );

  /**
   * Load all projects for current user
   *
   * @returns Array of user's projects, sorted by last modified
   *
   * @example
   * const projects = await loadProjects();
   */
  const loadProjects = useCallback(async (): Promise<Project[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const userId = getUserId();
      const projectsRef = collection(db, fsPath(userProjectsPath(userId)));

      // Query: order by last modified descending
      const q = query(projectsRef);
      const snapshot = await getDocs(q);

      const projects = snapshot.docs
        .map((doc) => convertDates(doc.data()))
        .sort((a, b) => b.lastModifiedAt.getTime() - a.lastModifiedAt.getTime());

      setIsLoading(false);
      return projects;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load projects';
      setError(errorMessage);
      setIsLoading(false);
      throw err;
    }
  }, [db, getUserId]);

  /**
   * Delete a project and associated files
   *
   * @param projectId - ID of project to delete
   * @throws Error if delete fails
   *
   * @example
   * await deleteProject('proj123');
   */
  const deleteProject = useCallback(
    async (projectId: string): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const userId = getUserId();
        const batch = writeBatch(db);

        // Delete project document
        const projectRef = doc(db, fsPath(projectPath(userId, projectId)));
        batch.delete(projectRef);

        // TODO: Delete associated subcollections (versions, comments, etc.)
        // Note: Firestore doesn't cascade deletes, so this needs manual handling

        // Commit batch
        await batch.commit();

        // Delete associated files from storage
        try {
          const thumbnailRef = ref(storage, `projects/${userId}/${projectId}/thumbnail`);
          await deleteObject(thumbnailRef);
        } catch (err) {
          // File might not exist, that's ok
          console.warn('Could not delete thumbnail:', err);
        }

        setIsLoading(false);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete project';
        setError(errorMessage);
        setIsLoading(false);
        throw err;
      }
    },
    [db, storage, getUserId]
  );

  /**
   * Subscribe to real-time updates for a single project
   *
   * @param projectId - ID of project to subscribe to
   * @param callback - Function called when project changes
   * @returns Unsubscribe function (must be called to stop listening)
   *
   * @example
   * const unsubscribe = subscribeToProject('proj123', (project) => {
   *   console.log('Project updated:', project);
   * });
   *
   * // Later, stop listening:
   * unsubscribe();
   */
  const subscribeToProject = useCallback(
    (projectId: string, callback: (project: Project) => void): Unsubscribe | null => {
      try {
        const userId = getUserId();
        const projectRef = doc(db, fsPath(projectPath(userId, projectId)));

        return onSnapshot(projectRef, (snapshot) => {
          if (snapshot.exists()) {
            const project = convertDates(snapshot.data());
            callback(project);
          }
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to subscribe to project';
        setError(errorMessage);
        return null;
      }
    },
    [db, getUserId]
  );

  /**
   * Subscribe to real-time updates for all user projects
   *
   * @param callback - Function called when projects change
   * @returns Unsubscribe function
   *
   * @example
   * const unsubscribe = subscribeToProjects((projects) => {
   *   console.log('Projects updated:', projects);
   * });
   */
  const subscribeToProjects = useCallback(
    (callback: (projects: Project[]) => void): Unsubscribe | null => {
      try {
        const userId = getUserId();
        const projectsRef = collection(db, fsPath(userProjectsPath(userId)));
        const q = query(projectsRef);

        return onSnapshot(q, (snapshot) => {
          const projects = snapshot.docs
            .map((doc) => convertDates(doc.data()))
            .sort((a, b) => b.lastModifiedAt.getTime() - a.lastModifiedAt.getTime());
          callback(projects);
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to subscribe to projects';
        setError(errorMessage);
        return null;
      }
    },
    [db, getUserId]
  );

  /**
   * Load current user's data
   *
   * @returns User object or null if not found
   *
   * @example
   * const userData = await loadUserData();
   */
  const loadUserData = useCallback(async (): Promise<User | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const userId = getUserId();
      const userRef = doc(db, fsPath(userPath(userId)));
      const snapshot = await getDoc(userRef);

      if (!snapshot.exists()) {
        setIsLoading(false);
        return null;
      }

      const userData = snapshot.data() as User;
      setIsLoading(false);
      return userData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user data';
      setError(errorMessage);
      setIsLoading(false);
      throw err;
    }
  }, [db, getUserId]);

  /**
   * Update current user's data
   *
   * @param userData - Partial user object with fields to update
   *
   * @example
   * await updateUserData({
   *   displayName: 'New Name',
   *   preferences: { darkMode: 'dark' },
   * });
   */
  const updateUserData = useCallback(
    async (userData: Partial<User>): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const userId = getUserId();
        const userRef = doc(db, fsPath(userPath(userId)));

        await updateDoc(userRef, {
          ...userData,
          updatedAt: Timestamp.fromDate(new Date()),
        });

        setIsLoading(false);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update user data';
        setError(errorMessage);
        setIsLoading(false);
        throw err;
      }
    },
    [db, getUserId]
  );

  /**
   * Clear error state
   */
  const clear = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    saveProject,
    loadProject,
    loadProjects,
    deleteProject,
    subscribeToProject,
    subscribeToProjects,
    loadUserData,
    updateUserData,
    clear,
  };
}
